import { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import { Save, Loader2, Eye, EyeOff, Plus, Pencil, Trash2, GripVertical, Star, Archive, EyeIcon } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format, addDays } from 'date-fns';
import ConfirmDialog from '../components/ConfirmDialog';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { Search, Calendar as CalendarIcon, PlayCircle, PauseCircle } from 'lucide-react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, collection, getDocs, addDoc, updateDoc, deleteDoc, writeBatch, Timestamp } from 'firebase/firestore';
import { SidebarProvider } from "@/components/ui/sidebar";
import OwnerSidebar from './OwnerSidebar';
import OwnerHeader from './OwnerHeader';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  badge: string;
  billingInterval: 'weekly' | 'monthly' | 'yearly' | 'one-time';
  prices: {
    KES: number;
    USD?: number;
  };
  customPricing: boolean;
  payWhatYouWant: boolean;
  freeTrialDays: number;
  setupFee: number;
  visibility: 'public' | 'hidden' | 'archived';
  order: number;
  features: string[];
  createdAt: string;
  updatedAt: string;
}

interface WorkspaceSubscription {
  id: string;
  workspaceName: string;
  ownerEmail: string;
  subscriptionStatus: string;
  subscriptionPlan: string;
  subscriptionExpiry: Timestamp | null;
  maxSeats?: number;
  institutionName?: string;
  organizationId?: string;
}

const DEFAULT_PLAN: Omit<SubscriptionPlan, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '',
  description: '',
  badge: '',
  billingInterval: 'monthly',
  prices: { KES: 0 },
  customPricing: false,
  payWhatYouWant: false,
  freeTrialDays: 0,
  setupFee: 0,
  visibility: 'public',
  order: 0,
  features: []
};

const CALLBACK_URL = 'https://us-central1-cbsms-da9ce.cloudfunctions.net/mpesaCallback';

const PaymentGateways = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [showSecrets, setShowSecrets] = useState(false);
  const [activeTab, setActiveTab] = useState('mpesa');
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);
  const [platformView, setPlatformView] = useState<'school' | 'independent'>('school');
  
  const [independentPricing, setIndependentPricing] = useState({
    monthly: { individual: "1,500", school: "20,000", durationDays: "30" },
    termly: { individual: "4,000", school: "55,000", durationDays: "90" },
    yearly: { individual: "12,000", school: "180,000", durationDays: "365" },
  });
  const [independentTrialDays, setIndependentTrialDays] = useState(14);
  const [defaultMaxSeats, setDefaultMaxSeats] = useState(10);

  const [indActiveTab, setIndActiveTab] = useState<'pesapal' | 'pricing'>('pesapal');
  const [pesapalConfig, setPesapalConfig] = useState({
    consumerKey: '', consumerSecret: '',
    environment: 'sandbox' as 'sandbox' | 'production',
    callbackBaseUrl: 'https://teach.sonoma.school/dashboard/billing',
    notificationId: '',
  });
  const [indShowSecrets, setIndShowSecrets] = useState(false);
  const [registeringIpn, setRegisteringIpn] = useState(false);
  
  // Subscription Plans State
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [newFeature, setNewFeature] = useState('');
  const [draggedPlan, setDraggedPlan] = useState<string | null>(null);
  
  // Global Settings State
  const [globalSettings, setGlobalSettings] = useState({
    defaultTrialDays: 14,
    allowTrialExtension: false,
    requireCardForTrial: false
  });
  
  const [config, setConfig] = useState({
    consumerKey: '',
    consumerSecret: '',
    passkey: '',
    shortcode: '',
    environment: 'sandbox',
    callbackUrl: CALLBACK_URL,
    accountReference: 'SchoolSub'
  });

  // Workspace Subscriptions State
  const [workspaces, setWorkspaces] = useState<WorkspaceSubscription[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceSubscription | null>(null);
  
  // Workspace Edit Dialog State
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editStatus, setEditStatus] = useState<string>("");
  const [editPlan, setEditPlan] = useState<string>("");
  const [editExpiry, setEditExpiry] = useState<Date | undefined>(undefined);
  const [editMaxSeats, setEditMaxSeats] = useState<number>(10);
  
  // Workspace Pause Confirm State
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [workspaceToToggle, setWorkspaceToToggle] = useState<WorkspaceSubscription | null>(null);

  const fetchWorkspaces = async () => {
    try {
      const workspacesRef = collection(db, "teaching_workspaces");
      const snapshot = await getDocs(workspacesRef);
      
      const workspacesData: WorkspaceSubscription[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const sub = data.subscription || {};
        workspacesData.push({
          id: docSnap.id,
          workspaceName: data.displayName || "Unknown Workspace",
          ownerEmail: data.email || "No Email",
          subscriptionStatus: sub.status || "none",
          subscriptionPlan: sub.plan || "free",
          subscriptionExpiry: sub.expiry || null,
          maxSeats: sub.maxSeats,
          institutionName: data.institutionName || data.displayName || "Unknown Institution",
          organizationId: data.organizationId || "Not assigned"
        });
      });

      setWorkspaces(workspacesData);
    } catch (error) {
      console.error("Error fetching workspaces:", error);
      toast({
        title: "Error",
        description: "Failed to load workspaces data.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        // Fetch M-Pesa config
        const mpesaRef = doc(db, 'system_config', 'mpesa_settings');
        const mpesaSnap = await getDoc(mpesaRef);
        if (mpesaSnap.exists()) {
          const data = mpesaSnap.data();
          setConfig(prev => ({ 
            ...prev, 
            ...data,
            callbackUrl: data.callbackUrl || CALLBACK_URL 
          }));
          setHasExistingConfig(true);
          setIsEditMode(false);
        } else {
          setIsEditMode(true);
        }

        // Fetch global subscription settings
        const globalRef = doc(db, 'system_config', 'subscription_settings');
        const globalSnap = await getDoc(globalRef);
        if (globalSnap.exists()) {
          setGlobalSettings(prev => ({ ...prev, ...globalSnap.data() }));
        }

        // Fetch independent pricing
        const indRef = doc(db, 'systemConfig', 'independentPricing');
        const indSnap = await getDoc(indRef);
        if (indSnap.exists()) {
          setIndependentPricing(indSnap.data() as any);
        }

        // Fetch independent trial days + seat capacity
        const trialRef = doc(db, 'system_config', 'independent_settings');
        const trialSnap = await getDoc(trialRef);
        if (trialSnap.exists()) {
          const d = trialSnap.data();
          if (d.trialDays) setIndependentTrialDays(d.trialDays);
          if (d.defaultMaxSeats) setDefaultMaxSeats(d.defaultMaxSeats);
        }

        // Fetch PesaPal public config
        const ppPublicRef = doc(db, 'systemConfig', 'pesapalPublicConfig');
        const ppPublicSnap = await getDoc(ppPublicRef);
        if (ppPublicSnap.exists()) {
          const d = ppPublicSnap.data() as any;
          setPesapalConfig(prev => ({ ...prev, environment: d.environment || 'sandbox', callbackBaseUrl: d.callbackBaseUrl || prev.callbackBaseUrl, notificationId: d.notificationId || '' }));
        }

        // Fetch PesaPal credentials (consumerKey/Secret stored in independentPaymentConfig)
        const ppCredRef = doc(db, 'systemConfig', 'independentPaymentConfig');
        const ppCredSnap = await getDoc(ppCredRef);
        if (ppCredSnap.exists()) {
          const d = ppCredSnap.data() as any;
          if (d.pesapal) setPesapalConfig(prev => ({ ...prev, consumerKey: d.pesapal.consumerKey || '', consumerSecret: d.pesapal.consumerSecret || '' }));
        }

        // Fetch subscription plans
        const plansRef = collection(db, 'subscription_plans');
        const plansSnap = await getDocs(plansRef);
        // Ensure we use the document ID as the primary ID, ignoring any empty 'id' field in the data
        const plansData = plansSnap.docs.map(d => {
            const data = d.data();
            return { 
                ...data, 
                id: d.id // Always use the document ID
            } as SubscriptionPlan;
        });
        setPlans(plansData.sort((a, b) => a.order - b.order));
        
        // Fetch Workspaces
        await fetchWorkspaces();
        
      } catch (error) {
        console.error("Error fetching config:", error);
        toast({
          title: "Error",
          description: "Failed to load settings.",
          variant: "destructive",
        });
      } finally {
        setFetching(false);
      }
    };

    fetchAll();
  }, []);

  const handleSaveMpesa = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, 'system_config', 'mpesa_settings'), {
        ...config,
        callbackUrl: config.callbackUrl || CALLBACK_URL,
        updatedAt: new Date().toISOString()
      });
      
      setHasExistingConfig(true);
      setIsEditMode(false);
      
      toast({
        title: "Settings Saved",
        description: "M-Pesa configuration has been updated successfully.",
      });
    } catch (error) {
      console.error("Error saving payment config:", error);
      toast({
        title: "Save Failed",
        description: "Could not save payment settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGlobalSettings = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, 'system_config', 'subscription_settings'), {
        ...globalSettings,
        updatedAt: new Date().toISOString()
      });
      
      toast({
        title: "Settings Saved",
        description: "Global subscription settings have been updated.",
      });
    } catch (error) {
      console.error("Error saving global settings:", error);
      toast({
        title: "Save Failed",
        description: "Could not save settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIndependentPricing = async () => {
    setLoading(true);
    try {
      // Save pricing
      await setDoc(doc(db, 'systemConfig', 'independentPricing'), {
        ...independentPricing,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      // Save trial days + faculty seat cap (single document, single source of truth)
      await setDoc(doc(db, 'system_config', 'independent_settings'), {
        trialDays: independentTrialDays,
        defaultMaxSeats: Number(defaultMaxSeats),
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      toast({
        title: "Settings Saved",
        description: "Independent platform pricing and trial period updated.",
      });
    } catch (error) {
      console.error("Error saving independent pricing:", error);
      toast({
        title: "Save Failed",
        description: "Could not save independent pricing. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePesapal = async () => {
    setLoading(true);
    try {
      // Save credentials into independentPaymentConfig
      const credRef = doc(db, 'systemConfig', 'independentPaymentConfig');
      await setDoc(credRef, { pesapal: { consumerKey: pesapalConfig.consumerKey, consumerSecret: pesapalConfig.consumerSecret }, updatedAt: new Date().toISOString() }, { merge: true });
      // Save public config separately
      const pubRef = doc(db, 'systemConfig', 'pesapalPublicConfig');
      await setDoc(pubRef, { environment: pesapalConfig.environment, callbackBaseUrl: pesapalConfig.callbackBaseUrl, updatedAt: new Date().toISOString() }, { merge: true });
      toast({ title: 'Saved', description: 'PesaPal config saved. Register IPN to complete setup.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to save PesaPal config.', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleRegisterIpn = async () => {
    setRegisteringIpn(true);
    try {
      const fns = getFunctions();
      const register = httpsCallable(fns, 'pesapalRegisterIpn');
      const result: any = await register({});
      setPesapalConfig(prev => ({ ...prev, notificationId: result.data?.notificationId || prev.notificationId }));
      toast({ title: 'IPN Registered', description: `Notification ID: ${result.data?.notificationId}` });
    } catch (err: any) {
      toast({ title: 'IPN Registration Failed', description: err.message, variant: 'destructive' });
    } finally { setRegisteringIpn(false); }
  };

  const handleIndependentPricingChange = (cycle: 'monthly'|'termly'|'yearly', field: 'individual'|'school'|'durationDays', value: string) => {
    setIndependentPricing(prev => ({
      ...prev,
      [cycle]: {
        ...prev[cycle],
        [field]: value
      }
    }));
  };

  const openPlanDialog = (plan?: SubscriptionPlan) => {
    if (plan) {
      setEditingPlan(plan);
    } else {
      setEditingPlan({
        ...DEFAULT_PLAN,
        id: '',
        order: plans.length,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as SubscriptionPlan);
    }
    setIsDialogOpen(true);
  };

  const handleSavePlan = async () => {
    if (!editingPlan || !editingPlan.name) {
      toast({ title: "Error", description: "Plan name is required.", variant: "destructive" });
      return;
    }

    setPlanLoading(true);
    try {
      const planData = {
        ...editingPlan,
        updatedAt: new Date().toISOString()
      };

      if (editingPlan.id) {
        // Update existing plan - include id in the document
        await updateDoc(doc(db, 'subscription_plans', editingPlan.id), {
          ...planData,
          id: editingPlan.id,
          updatedAt: new Date().toISOString()
        });
        setPlans(prev => prev.map(p => p.id === editingPlan.id ? { ...planData, id: editingPlan.id } : p));
      } else {
        // Create new plan - first create, then update with id
        const docRef = await addDoc(collection(db, 'subscription_plans'), {
          ...planData,
          createdAt: new Date().toISOString()
        });
        // Update the document to include its own ID
        await updateDoc(docRef, { id: docRef.id });
        setPlans(prev => [...prev, { ...planData, id: docRef.id }]);
      }

      toast({ title: "Success", description: `Plan "${editingPlan.name}" saved successfully.` });
      setIsDialogOpen(false);
      setEditingPlan(null);
    } catch (error) {
      console.error("Error saving plan:", error);
      toast({ title: "Error", description: "Failed to save plan.", variant: "destructive" });
    } finally {
      setPlanLoading(false);
    }
  };

  const handleWorkspaceEditClick = (workspace: WorkspaceSubscription) => {
    setSelectedWorkspace(workspace);
    setEditStatus(workspace.subscriptionStatus);
    setEditPlan(workspace.subscriptionPlan);
    setEditExpiry(workspace.subscriptionExpiry ? workspace.subscriptionExpiry.toDate() : undefined);
    setEditMaxSeats(workspace.maxSeats ?? defaultMaxSeats);
    setIsEditDialogOpen(true);
  };

  const handleWorkspacePauseToggle = (workspace: WorkspaceSubscription) => {
    setWorkspaceToToggle(workspace);
    setIsConfirmDialogOpen(true);
  };

  const confirmToggleWorkspaceSubscription = async () => {
    if (!workspaceToToggle) return;
    const newStatus = workspaceToToggle.subscriptionStatus === 'paused' ? 'active' : 'paused';
    try {
      const workspaceRef = doc(db, `teaching_workspaces/${workspaceToToggle.id}`);
      await updateDoc(workspaceRef, {
        "subscription.status": newStatus
      });
      toast({
        title: newStatus === 'paused' ? "Subscription Paused" : "Subscription Resumed",
        description: `${workspaceToToggle.workspaceName} is now ${newStatus}.`,
      });
      fetchWorkspaces();
    } catch (err) {
      console.error(err);
      toast({ title: "Error", description: "Failed to toggle status", variant: "destructive" });
    } finally {
      setIsConfirmDialogOpen(false);
      setWorkspaceToToggle(null);
    }
  };

  const handleSaveWorkspaceSubscription = async () => {
    if (!selectedWorkspace) return;

    try {
      const workspaceRef = doc(db, `teaching_workspaces/${selectedWorkspace.id}`);
      await updateDoc(workspaceRef, {
        "subscription.status": editStatus,
        "subscription.plan": editPlan,
        "subscription.expiry": editExpiry ? Timestamp.fromDate(editExpiry) : null,
        "subscription.maxSeats": Number(editMaxSeats)
      });

      toast({
        title: "Success",
        description: "Workspace subscription updated successfully.",
      });

      setIsEditDialogOpen(false);
      fetchWorkspaces();
    } catch (error) {
      console.error("Error saving workspace subscription:", error);
      toast({
        title: "Error",
        description: "Failed to update workspace.",
        variant: "destructive",
      });
    }
  };

  const handleDeletePlan = async (planId: string) => {
    if (!confirm("Are you sure you want to delete this plan?")) return;
    
    // Handle case where planId is empty (legacy bad data)
    if (!planId) {
        // If we have a plan in state with empty ID, we can't delete it from Firestore easily
        // unless we know its document ID. But if the state has empty ID, we are stuck.
        // However, the user might be clicking the delete button on a card.
        // If the plan.id passed here is empty, it means the state is corrupted.
        // We should try to reload the plans to get fresh IDs if possible, or just remove from UI.
        
        // If the plan was created with the old code, the document exists but has empty 'id' field inside data.
        // But the 'id' prop passed to this function comes from the plan object in the array.
        // In fetchAll(), we do: plansData = plansSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        // So 'id' should be the document ID (which is never empty), UNLESS d.data() overwrites it with an empty 'id' field.
        // Let's fix the fetch logic to prioritize d.id
        
        toast({ title: "Error", description: "Invalid plan ID. Please refresh the page.", variant: "destructive" });
        return;
    }

    try {
      await deleteDoc(doc(db, 'subscription_plans', planId));
      setPlans(prev => prev.filter(p => p.id !== planId));
      toast({ title: "Deleted", description: "Plan has been deleted." });
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast({ title: "Error", description: "Failed to delete plan.", variant: "destructive" });
    }
  };

  const addFeature = () => {
    if (newFeature.trim() && editingPlan) {
      setEditingPlan({
        ...editingPlan,
        features: [...editingPlan.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    if (editingPlan) {
      setEditingPlan({
        ...editingPlan,
        features: editingPlan.features.filter((_, i) => i !== index)
      });
    }
  };

  const handleDragStart = (planId: string) => {
    setDraggedPlan(planId);
  };

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedPlan || draggedPlan === targetId) return;
  };

  const handleDrop = async (targetId: string) => {
    if (!draggedPlan || draggedPlan === targetId) return;

    const draggedIndex = plans.findIndex(p => p.id === draggedPlan);
    const targetIndex = plans.findIndex(p => p.id === targetId);
    
    const newPlans = [...plans];
    const [removed] = newPlans.splice(draggedIndex, 1);
    newPlans.splice(targetIndex, 0, removed);
    
    // Update order for all plans
    const reorderedPlans = newPlans.map((p, i) => ({ ...p, order: i }));
    setPlans(reorderedPlans);
    setDraggedPlan(null);

    // Save new order to Firestore
    try {
      const batch = writeBatch(db);
      reorderedPlans.forEach(plan => {
        batch.update(doc(db, 'subscription_plans', plan.id), { order: plan.order });
      });
      await batch.commit();
    } catch (error) {
      console.error("Error reordering plans:", error);
    }
  };

  const getVisibilityBadge = (visibility: string) => {
    switch (visibility) {
      case 'public': return <Badge className="bg-green-100 text-green-800"><EyeIcon className="h-3 w-3 mr-1" />Public</Badge>;
      case 'hidden': return <Badge className="bg-yellow-100 text-yellow-800"><EyeOff className="h-3 w-3 mr-1" />Hidden</Badge>;
      case 'archived': return <Badge className="bg-gray-100 text-gray-800"><Archive className="h-3 w-3 mr-1" />Archived</Badge>;
      default: return null;
    }
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <OwnerSidebar activeSection="/owner/payment-gateways" onSectionChange={() => {}} userRole="owner" />
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <OwnerHeader />
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            <div className="max-w-6xl mx-auto space-y-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Payment & Subscriptions</h1>
                  <p className="text-gray-500 dark:text-gray-400 mt-2">Configure payment gateways and manage subscription plans.</p>
                </div>
                {/* Platform Toggle */}
                <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-fit">
                  <Button 
                    variant={platformView === 'school' ? 'default' : 'ghost'} 
                    size="sm"
                    onClick={() => setPlatformView('school')}
                    className="rounded-md"
                  >
                    School Management System
                  </Button>
                  <Button 
                    variant={platformView === 'independent' ? 'default' : 'ghost'} 
                    size="sm"
                    onClick={() => setPlatformView('independent')}
                    className="rounded-md"
                  >
                    Independent Platform
                  </Button>
                </div>
              </div>

              {platformView === 'school' ? (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="mpesa">M-Pesa Settings</TabsTrigger>
                  <TabsTrigger value="plans">Subscription Plans</TabsTrigger>
                  <TabsTrigger value="global">Global Settings</TabsTrigger>
                </TabsList>

                {/* M-Pesa Tab */}
                <TabsContent value="mpesa" className="mt-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>M-Pesa Daraja API Configuration</span>
                        <div className="flex gap-2">
                          {hasExistingConfig && !isEditMode && (
                            <Button variant="outline" size="sm" onClick={() => setIsEditMode(true)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </Button>
                          )}
                          {isEditMode && (
                            <Button variant="ghost" size="sm" onClick={() => setShowSecrets(!showSecrets)}>
                              {showSecrets ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                              {showSecrets ? 'Hide Secrets' : 'Show Secrets'}
                            </Button>
                          )}
                        </div>
                      </CardTitle>
                      <CardDescription>
                        Enter your Daraja API credentials here. These will be used to process school subscriptions.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {fetching ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        </div>
                      ) : hasExistingConfig && !isEditMode ? (
                        // View Mode - Show saved configuration
                        <div className="space-y-6 animate-in fade-in duration-500">
                          
                          {/* Status Cards */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 m-2">
                            <div className="p-5 bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm">
                              <div className="flex items-center justify-between mb-2">
                                <Label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Environment</Label>
                                <div className={`h-2 w-2 rounded-full ${config.environment === 'production' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-semibold text-gray-900 dark:text-white capitalize">{config.environment}</span>
                                <Badge variant={config.environment === 'production' ? 'default' : 'outline'} className="ml-2">
                                  {config.environment === 'production' ? 'Live' : 'Test Mode'}
                                </Badge>
                              </div>
                            </div>

                            <div className="p-5 bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm">
                              <Label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-2">Shortcode</Label>
                              <div className="flex items-baseline gap-1">
                                <span className="text-2xl font-mono font-bold text-gray-900 dark:text-white tracking-tight">{config.shortcode || '---'}</span>
                                <span className="text-xs text-gray-400">Paybill/Till</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Credentials Card */}
                          <div className="bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden m-2">
                            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-100/50 dark:bg-gray-800/50 flex items-center gap-2">
                              <div className="h-4 w-1 bg-blue-500 rounded-full"></div>
                              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">API Credentials</h3>
                            </div>
                            <div className="p-5 space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">Consumer Key</Label>
                                  <div className="font-mono text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 px-3 py-2 rounded border border-gray-200 dark:border-gray-700">
                                    {'•'.repeat(24)}{config.consumerKey.slice(-4)}
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">Consumer Secret</Label>
                                  <div className="font-mono text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 px-3 py-2 rounded border border-gray-200 dark:border-gray-700">
                                    {'•'.repeat(24)}{config.consumerSecret.slice(-4)}
                                  </div>
                                </div>
                              </div>
                              <div>
                                <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">Passkey</Label>
                                <div className="font-mono text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 px-3 py-2 rounded border border-gray-200 dark:border-gray-700 truncate">
                                  {'•'.repeat(32)}{config.passkey.slice(-4)}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Integration Details */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 m-2">
                            <div className="md:col-span-2 p-4 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-xl">
                              <Label className="text-xs font-semibold text-blue-600/70 dark:text-blue-400/70 uppercase tracking-wider block mb-1">Callback URL</Label>
                              <div className="font-mono text-xs text-blue-600 dark:text-blue-400 break-all">
                                {config.callbackUrl}
                              </div>
                            </div>
                            
                            <div className="p-4 bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl">
                              <Label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">Account Ref</Label>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {config.accountReference || 'SchoolSub'}
                              </div>
                            </div>
                          </div>
                          
                          <div className="pt-2 flex justify-end">
                            <Button variant="outline" onClick={() => setIsEditMode(true)} className="hover:bg-gray-100 dark:hover:bg-gray-800">
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit Configuration
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Edit Mode - Show form inputs
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                          
                          {/* Environment & Shortcode Section */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Environment</Label>
                              <Select 
                                value={config.environment} 
                                onValueChange={(val) => setConfig({...config, environment: val})}
                              >
                                <SelectTrigger className="h-10">
                                  <SelectValue placeholder="Select Environment" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                                  <SelectItem value="production">Production (Live)</SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-[10px] text-gray-500">Select 'Sandbox' for testing with virtual money.</p>
                            </div>
                            
                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Shortcode (Paybill/Till)</Label>
                              <div className="relative">
                                <Input 
                                  value={config.shortcode}
                                  onChange={(e) => setConfig({...config, shortcode: e.target.value})}
                                  placeholder="e.g. 174379"
                                  className="pl-9 h-10 font-mono"
                                />
                                <div className="absolute left-3 top-2.5 text-gray-400">
                                  <span className="text-xs">#</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* API Credentials Section */}
                          <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-xl border border-gray-100 dark:border-gray-700 space-y-4">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-4">
                              <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                              API Credentials
                            </h3>
                            
                            <div className="grid gap-4">
                              <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider text-gray-500">Consumer Key</Label>
                                <div className="relative">
                                  <Input 
                                    type={showSecrets ? "text" : "password"}
                                    value={config.consumerKey}
                                    onChange={(e) => setConfig({...config, consumerKey: e.target.value})}
                                    placeholder="Enter Consumer Key"
                                    className="font-mono text-sm pr-10"
                                  />
                                </div>
                              </div>

                              <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider text-gray-500">Consumer Secret</Label>
                                <Input 
                                  type={showSecrets ? "text" : "password"}
                                  value={config.consumerSecret}
                                  onChange={(e) => setConfig({...config, consumerSecret: e.target.value})}
                                  placeholder="Enter Consumer Secret"
                                  className="font-mono text-sm"
                                />
                              </div>

                              <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider text-gray-500">Passkey</Label>
                                <Input 
                                  type={showSecrets ? "text" : "password"}
                                  value={config.passkey}
                                  onChange={(e) => setConfig({...config, passkey: e.target.value})}
                                  placeholder="Enter Lipa na M-Pesa Online Passkey"
                                  className="font-mono text-sm"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Integration Settings */}
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Callback URL</Label>
                              <Input 
                                value={config.callbackUrl}
                                onChange={(e) => setConfig({...config, callbackUrl: e.target.value})}
                                placeholder={CALLBACK_URL}
                                className="bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-800 text-blue-600 font-mono text-xs"
                              />
                              <p className="text-[11px] text-gray-500 flex items-center gap-1">
                                <div className="h-1 w-1 rounded-full bg-blue-400"></div>
                                Auto-filled with your Firebase function URL. Only change if you have a custom callback endpoint.
                              </p>
                            </div>
                            
                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Account Reference</Label>
                              <Input 
                                value={config.accountReference}
                                onChange={(e) => setConfig({...config, accountReference: e.target.value})}
                                placeholder="e.g. SchoolSub"
                                className="max-w-md"
                              />
                              <p className="text-[11px] text-gray-500">This will appear on the M-Pesa transaction statement.</p>
                            </div>
                          </div>

                          <div className="pt-6 flex justify-end gap-3 border-t border-gray-100 dark:border-gray-800">
                            {hasExistingConfig && (
                              <Button variant="outline" onClick={() => setIsEditMode(false)} className="px-6">
                                Cancel
                              </Button>
                            )}
                            <Button onClick={handleSaveMpesa} disabled={loading} className="bg-blue-600 hover:bg-blue-700 px-6 shadow-lg shadow-blue-600/20">
                              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                              Save Configuration
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Subscription Plans Tab */}
                <TabsContent value="plans" className="mt-6 space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-xl font-semibold">Subscription Plans</h2>
                      <p className="text-sm text-gray-500">Drag to reorder plans. The order will be reflected on the pricing page.</p>
                    </div>
                    <Button onClick={() => openPlanDialog()} className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Plan
                    </Button>
                  </div>

                  {fetching ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                  ) : plans.length === 0 ? (
                    <Card className="p-8 text-center">
                      <p className="text-gray-500">No subscription plans created yet.</p>
                      <Button onClick={() => openPlanDialog()} variant="link">Create your first plan</Button>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {plans.map((plan) => (
                        <Card 
                          key={plan.id}
                          draggable
                          onDragStart={() => handleDragStart(plan.id)}
                          onDragOver={(e) => handleDragOver(e, plan.id)}
                          onDrop={() => handleDrop(plan.id)}
                          className={`cursor-move transition-all ${draggedPlan === plan.id ? 'opacity-50 border-blue-500' : ''} ${plan.visibility === 'archived' ? 'opacity-60' : ''}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <GripVertical className="h-5 w-5 text-gray-400 cursor-grab" />
                              
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-lg">{plan.name}</h3>
                                  {plan.badge && <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-yellow-100 text-yellow-800 border-transparent"><Star className="h-3 w-3 mr-1" />{plan.badge}</div>}
                                  {getVisibilityBadge(plan.visibility)}
                                </div>
                                <p className="text-sm text-gray-500 mt-1">{plan.description || 'No description'}</p>
                                <div className="flex items-center gap-4 mt-2 text-sm">
                                  <span className="font-bold text-green-600">KES {plan.prices.KES?.toLocaleString() || 0}</span>
                                  <span className="text-gray-400">/ {plan.billingInterval}</span>
                                  {plan.freeTrialDays > 0 && <Badge variant="outline">{plan.freeTrialDays} day trial</Badge>}
                                  {plan.setupFee > 0 && <Badge variant="outline">KES {plan.setupFee} setup fee</Badge>}
                                </div>
                              </div>

                              <div className="flex gap-2">
                                <Button variant="ghost" size="sm" onClick={() => openPlanDialog(plan)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDeletePlan(plan.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Global Settings Tab */}
                <TabsContent value="global" className="mt-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Global Subscription Settings</CardTitle>
                      <CardDescription>Default settings that apply to all subscription plans.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-2">
                        <Label>Default Free Trial Period (Days)</Label>
                        <Input 
                          type="number"
                          value={globalSettings.defaultTrialDays}
                          onChange={(e) => setGlobalSettings({...globalSettings, defaultTrialDays: parseInt(e.target.value) || 0})}
                          placeholder="14"
                          min="0"
                        />
                        <p className="text-xs text-gray-500">New schools will get this many days of free trial.</p>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Allow Trial Extension</Label>
                          <p className="text-xs text-gray-500">Allow admins to request trial extensions.</p>
                        </div>
                        <Switch 
                          checked={globalSettings.allowTrialExtension}
                          onCheckedChange={(checked) => setGlobalSettings({...globalSettings, allowTrialExtension: checked})}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Require Payment Method for Trial</Label>
                          <p className="text-xs text-gray-500">Schools must add a payment method to start trial.</p>
                        </div>
                        <Switch 
                          checked={globalSettings.requireCardForTrial}
                          onCheckedChange={(checked) => setGlobalSettings({...globalSettings, requireCardForTrial: checked})}
                        />
                      </div>
                    </CardContent>
                    <CardFooter className="justify-end">
                      <Button onClick={handleSaveGlobalSettings} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Global Settings
                      </Button>
                    </CardFooter>
                  </Card>
                </TabsContent>
              </Tabs>
              ) : (
                <Tabs value={indActiveTab} onValueChange={(val: any) => setIndActiveTab(val)} className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="pesapal">PesaPal Settings</TabsTrigger>
                    <TabsTrigger value="pricing">Independent Pricing</TabsTrigger>
                    <TabsTrigger value="workspaces">Workspaces & Subscriptions</TabsTrigger>
                  </TabsList>

                  {/* ── PesaPal Tab ── */}
                  <TabsContent value="pesapal" className="mt-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          PesaPal API 3.0 — Independent Platform
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setIndShowSecrets(!indShowSecrets)}>
                              {indShowSecrets ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                              {indShowSecrets ? 'Hide' : 'Show'} Keys
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => {
                              setPesapalConfig(prev => ({ ...prev, consumerKey: 'qg0Tnqv5ZGaEBXM8p37j7nVWr38iFvTI', consumerSecret: 'osVzr4Q5c7bkGhRbPKqNMLjNfBbFdR7/SV3F5bO6LXw=', environment: 'sandbox', callbackBaseUrl: 'https://teach.sonoma.school/dashboard/billing' }));
                              toast({ title: 'Sandbox data filled', description: 'PesaPal sandbox credentials loaded.' });
                            }}>
                              Autofill Sandbox
                            </Button>
                          </div>
                        </CardTitle>
                        <CardDescription>
                          Credentials are stored in <code>systemConfig/independentPaymentConfig</code>. The public config (environment, callback URL, IPN ID) goes into <code>systemConfig/pesapalPublicConfig</code>.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Environment</Label>
                            <Select value={pesapalConfig.environment} onValueChange={(v: any) => setPesapalConfig(p => ({ ...p, environment: v }))}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                                <SelectItem value="production">Production (Live)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Callback Base URL</Label>
                            <Input value={pesapalConfig.callbackBaseUrl} onChange={e => setPesapalConfig(p => ({ ...p, callbackBaseUrl: e.target.value }))} placeholder="https://teach.sonoma.school/dashboard/billing" />
                            <p className="text-[10px] text-gray-500">PesaPal will append <code>?pesapal_callback=1</code> to this URL.</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Consumer Key</Label>
                          <Input type={indShowSecrets ? 'text' : 'password'} value={pesapalConfig.consumerKey} onChange={e => setPesapalConfig(p => ({ ...p, consumerKey: e.target.value }))} placeholder="PesaPal Consumer Key" className="font-mono text-sm" />
                        </div>
                        <div className="space-y-2">
                          <Label>Consumer Secret</Label>
                          <Input type={indShowSecrets ? 'text' : 'password'} value={pesapalConfig.consumerSecret} onChange={e => setPesapalConfig(p => ({ ...p, consumerSecret: e.target.value }))} placeholder="PesaPal Consumer Secret" className="font-mono text-sm" />
                        </div>
                        {pesapalConfig.notificationId && (
                          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                            <p className="text-xs font-semibold text-green-700 dark:text-green-400">IPN Registered ✓</p>
                            <p className="text-xs font-mono text-green-600 dark:text-green-500 break-all mt-1">{pesapalConfig.notificationId}</p>
                          </div>
                        )}
                      </CardContent>
                      <CardFooter className="flex gap-3 justify-end border-t pt-4">
                        <Button variant="outline" onClick={handleRegisterIpn} disabled={registeringIpn || !pesapalConfig.consumerKey}>
                          {registeringIpn ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                          {pesapalConfig.notificationId ? 'Re-Register IPN' : 'Register IPN (Required)'}
                        </Button>
                        <Button onClick={handleSavePesapal} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                          Save PesaPal Config
                        </Button>
                      </CardFooter>
                    </Card>
                  </TabsContent>

                  {/* ── Pricing Tab ── */}
                  <TabsContent value="pricing" className="mt-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Independent Platform Pricing</CardTitle>
                        <CardDescription>Pricing for independent teachers and school enterprise packs. These values are used by the Cloud Functions to resolve canonical amounts — the client never sets the price.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Trial Days + Faculty Seats — all independent platform defaults in one place */}
                        <div className="space-y-2 border p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700">
                          <h3 className="text-base font-semibold">Free Trial Period</h3>
                          <div className="flex items-center gap-4">
                            <div className="space-y-1 flex-1">
                              <Label>Trial Duration (Days)</Label>
                              <Input
                                type="number"
                                value={independentTrialDays}
                                onChange={(e) => setIndependentTrialDays(parseInt(e.target.value) || 0)}
                                placeholder="14"
                                min="0"
                                max="365"
                                className="max-w-[140px]"
                              />
                              <p className="text-xs text-gray-500">Applies to both Individual and Faculty trial accounts on the independent platform.</p>
                            </div>
                          </div>
                        </div>

                        {/* Faculty Seat Capacity */}
                        <div className="space-y-2 border p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
                          <h3 className="text-base font-semibold">Faculty Seat Capacity</h3>
                          <div className="flex items-center gap-4">
                            <div className="space-y-1 flex-1">
                              <Label>Default Max Seats (Faculty Plan)</Label>
                              <Input
                                type="number"
                                value={defaultMaxSeats}
                                onChange={(e) => setDefaultMaxSeats(parseInt(e.target.value) || 1)}
                                placeholder="10"
                                min="1"
                                max="500"
                                className="max-w-[140px]"
                              />
                              <p className="text-xs text-gray-500">Maximum number of teacher seats included in a Faculty plan subscription.</p>
                            </div>
                          </div>
                        </div>

                        {(['monthly', 'termly', 'yearly'] as const).map((cycle) => (
                          <div key={cycle} className="space-y-4 border p-4 rounded-lg bg-white dark:bg-gray-800/50">
                            <h3 className="text-lg font-semibold capitalize">{cycle} Cycle</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="space-y-2">
                                <Label>Individual Plan (KSH)</Label>
                                <Input value={independentPricing[cycle].individual} onChange={(e) => handleIndependentPricingChange(cycle, 'individual', e.target.value)} placeholder="e.g. 1,500" />
                              </div>
                              <div className="space-y-2">
                                <Label>Faculty Plan (KSH)</Label>
                                <Input value={independentPricing[cycle].school} onChange={(e) => handleIndependentPricingChange(cycle, 'school', e.target.value)} placeholder="e.g. 20,000" />
                              </div>
                              <div className="space-y-2">
                                <Label>Cycle Duration (Days)</Label>
                                <Input type="number" value={independentPricing[cycle].durationDays || ''} onChange={(e) => handleIndependentPricingChange(cycle, 'durationDays', e.target.value)} placeholder="e.g. 30" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                      <CardFooter className="justify-end bg-gray-50 dark:bg-gray-800/30 pt-4 rounded-b-xl border-t dark:border-gray-700">
                        <Button onClick={handleSaveIndependentPricing} disabled={loading} className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20">
                          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                          Save Pricing Configuration
                        </Button>
                      </CardFooter>
                    </Card>
                  </TabsContent>

                  {/* ── Workspaces & Subscriptions Tab ── */}
                  <TabsContent value="workspaces" className="mt-6">
                    <Card>
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <CardTitle>Independent Teachers & Facilities</CardTitle>
                          <div className="flex gap-2">
                            <div className="relative w-64">
                              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                              <Input
                                placeholder="Search workspaces..."
                                className="pl-8"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                              />
                            </div>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                              <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Filter by status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Statuses</SelectItem>
                                <SelectItem value="active">Active (Paid)</SelectItem>
                                <SelectItem value="trial">Trial</SelectItem>
                                <SelectItem value="paused">Paused</SelectItem>
                                <SelectItem value="expired">Expired (Unpaid)</SelectItem>
                                <SelectItem value="none">No Subscription</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {loading ? (
                          <div className="flex justify-center p-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          </div>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Workspace Name</TableHead>
                                <TableHead>Institution Name</TableHead>
                                <TableHead>Org ID</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Plan</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Expiry Date</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {workspaces
                                .filter(w => (statusFilter === 'all' || w.subscriptionStatus === statusFilter) &&
                                            (w.workspaceName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                             w.ownerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                             (w.institutionName && w.institutionName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                             (w.organizationId && w.organizationId.toLowerCase().includes(searchTerm.toLowerCase()))))
                                .map((workspace) => (
                                <TableRow key={workspace.id}>
                                  <TableCell className="font-medium">{workspace.workspaceName}</TableCell>
                                  <TableCell>{workspace.institutionName}</TableCell>
                                  <TableCell className="font-mono text-xs">{workspace.organizationId}</TableCell>
                                  <TableCell>{workspace.ownerEmail}</TableCell>
                                  <TableCell className="capitalize">{workspace.subscriptionPlan}</TableCell>
                                  <TableCell>
                                    <Badge variant={workspace.subscriptionStatus === 'active' ? 'default' : workspace.subscriptionStatus === 'trial' ? 'outline' : workspace.subscriptionStatus === 'paused' ? 'destructive' : 'secondary'} className={workspace.subscriptionStatus === 'active' ? 'bg-green-500' : workspace.subscriptionStatus === 'trial' ? 'bg-blue-500' : workspace.subscriptionStatus === 'paused' ? 'bg-yellow-500' : ''}>
                                      {workspace.subscriptionStatus}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    {workspace.subscriptionExpiry 
                                      ? format(workspace.subscriptionExpiry.toDate(), "PPP") 
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex justify-end gap-2">
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        title={workspace.subscriptionStatus === 'paused' ? "Resume Subscription" : "Pause Subscription"}
                                        onClick={() => handleWorkspacePauseToggle(workspace)}
                                      >
                                        {workspace.subscriptionStatus === 'paused' ? (
                                          <PlayCircle className="h-4 w-4 text-green-600" />
                                        ) : (
                                          <PauseCircle className="h-4 w-4 text-yellow-600" />
                                        )}
                                      </Button>
                                      <Button variant="ghost" size="sm" onClick={() => handleWorkspaceEditClick(workspace)}>
                                        Edit
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {workspaces.filter(w => (statusFilter === 'all' || w.subscriptionStatus === statusFilter) &&
                                            (w.workspaceName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                             w.ownerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                             (w.institutionName && w.institutionName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                             (w.organizationId && w.organizationId.toLowerCase().includes(searchTerm.toLowerCase())))).length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                    No independent workspaces found.
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Plan Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan?.id ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
            <DialogDescription>Configure the subscription plan details below.</DialogDescription>
          </DialogHeader>

          {editingPlan && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Plan Name *</Label>
                  <Input 
                    value={editingPlan.name}
                    onChange={(e) => setEditingPlan({...editingPlan, name: e.target.value})}
                    placeholder="e.g. Basic, Pro, Enterprise"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Badge (Optional)</Label>
                  <Input 
                    value={editingPlan.badge}
                    onChange={(e) => setEditingPlan({...editingPlan, badge: e.target.value})}
                    placeholder="e.g. Most Popular, Best Value"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea 
                  value={editingPlan.description}
                  onChange={(e) => setEditingPlan({...editingPlan, description: e.target.value})}
                  placeholder="Describe what's included in this plan..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Billing Interval</Label>
                  <Select 
                    value={editingPlan.billingInterval}
                    onValueChange={(val: any) => setEditingPlan({...editingPlan, billingInterval: val})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                      <SelectItem value="one-time">One-Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Visibility</Label>
                  <Select 
                    value={editingPlan.visibility}
                    onValueChange={(val: any) => setEditingPlan({...editingPlan, visibility: val})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="hidden">Hidden</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Price (KES) *</Label>
                  <Input 
                    type="number"
                    value={editingPlan.prices.KES}
                    onChange={(e) => setEditingPlan({...editingPlan, prices: {...editingPlan.prices, KES: parseFloat(e.target.value) || 0}})}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price (USD) - Optional</Label>
                  <Input 
                    type="number"
                    value={editingPlan.prices.USD || ''}
                    onChange={(e) => setEditingPlan({...editingPlan, prices: {...editingPlan.prices, USD: parseFloat(e.target.value) || undefined}})}
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Free Trial Days</Label>
                  <Input 
                    type="number"
                    value={editingPlan.freeTrialDays}
                    onChange={(e) => setEditingPlan({...editingPlan, freeTrialDays: parseInt(e.target.value) || 0})}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Setup Fee (KES)</Label>
                  <Input 
                    type="number"
                    value={editingPlan.setupFee}
                    onChange={(e) => setEditingPlan({...editingPlan, setupFee: parseFloat(e.target.value) || 0})}
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label>Custom Pricing</Label>
                    <p className="text-xs text-gray-500">Allow negotiated pricing</p>
                  </div>
                  <Switch 
                    checked={editingPlan.customPricing}
                    onCheckedChange={(checked) => setEditingPlan({...editingPlan, customPricing: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label>Pay What You Want</Label>
                    <p className="text-xs text-gray-500">Let users set their price</p>
                  </div>
                  <Switch 
                    checked={editingPlan.payWhatYouWant}
                    onCheckedChange={(checked) => setEditingPlan({...editingPlan, payWhatYouWant: checked})}
                  />
                </div>
              </div>

              {/* Features Section */}
              <div className="space-y-2">
                <Label>Features</Label>
                <div className="flex gap-2">
                  <Input 
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="Add a feature..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <Button type="button" variant="outline" onClick={addFeature}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-1 mt-2">
                  {editingPlan.features.map((feature, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <span className="text-sm">{feature}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeFeature(i)}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePlan} disabled={planLoading} className="bg-blue-600 hover:bg-blue-700">
              {planLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {editingPlan?.id ? 'Update Plan' : 'Create Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Workspace Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Workspace Subscription</DialogTitle>
            <DialogDescription>
              Update subscription details for {selectedWorkspace?.workspaceName}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">Status</Label>
              <Select 
                value={editStatus} 
                onValueChange={(value) => {
                  setEditStatus(value);
                  if (value === 'trial') {
                    setEditExpiry(addDays(new Date(), independentTrialDays || 14));
                  }
                }}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="none">No Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="plan" className="text-right">Plan</Label>
              <Select value={editPlan} onValueChange={setEditPlan}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual_trial">Individual (Free Trial)</SelectItem>
                  <SelectItem value="faculty_trial">Faculty (Free Trial)</SelectItem>
                  <SelectItem value="individual_monthly">Individual Monthly</SelectItem>
                  <SelectItem value="faculty_monthly">Faculty Monthly</SelectItem>
                  <SelectItem value="individual_termly">Individual Termly</SelectItem>
                  <SelectItem value="faculty_termly">Faculty Termly</SelectItem>
                  <SelectItem value="individual_yearly">Individual Yearly</SelectItem>
                  <SelectItem value="faculty_yearly">Faculty Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Expiry</Label>
              <div className="col-span-3 flex flex-col gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !editExpiry && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editExpiry ? format(editExpiry, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={editExpiry} onSelect={setEditExpiry} initialFocus />
                  </PopoverContent>
                </Popover>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => setEditExpiry(addDays(editExpiry || new Date(), 7))}>+7 Days</Button>
                  <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => setEditExpiry(addDays(editExpiry || new Date(), 14))}>+14 Days</Button>
                  <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => setEditExpiry(addDays(editExpiry || new Date(), 30))}>+30 Days</Button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="maxSeats" className="text-right text-xs">Faculty Seats Cap</Label>
              <Input
                id="maxSeats"
                type="number"
                min={1}
                max={500}
                value={editMaxSeats}
                onChange={(e) => setEditMaxSeats(Number(e.target.value))}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSaveWorkspaceSubscription}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workspace Pause Confirmation Dialog */}
      <ConfirmDialog
        open={isConfirmDialogOpen}
        title={workspaceToToggle?.subscriptionStatus === 'paused' ? "Resume Subscription?" : "Pause Subscription?"}
        description={workspaceToToggle?.subscriptionStatus === 'paused' 
          ? `Are you sure you want to resume access for ${workspaceToToggle?.workspaceName}?`
          : `Are you sure you want to pause access for ${workspaceToToggle?.workspaceName}? Users will be blocked.`
        }
        confirmText={workspaceToToggle?.subscriptionStatus === 'paused' ? "Resume Access" : "Pause Access"}
        cancelText="Cancel"
        onConfirm={confirmToggleWorkspaceSubscription}
        onCancel={() => setIsConfirmDialogOpen(false)}
      />

    </SidebarProvider>
  );
};

export default PaymentGateways;
